import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { formatPhoneNumber } from "@/lib/phoneNumberUtils";
import { WhatsAppNumber, whatsappService } from "@/services/whatsappService";
import { Loader2, Phone, Plus, QrCode, Trash2, RefreshCw, MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";

interface WhatsAppNumbersTabProps {
  organizationId: string;
}

export default function WhatsAppNumbersTab({
  organizationId,
}: WhatsAppNumbersTabProps) {
  const [whatsappNumbers, setWhatsappNumbers] = useState<WhatsAppNumber[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isQRDialogOpen, setIsQRDialogOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<any>(null);
  const [deleteConfirmNumber, setDeleteConfirmNumber] =
    useState<WhatsAppNumber | null>(null);
  const [selectedNumber, setSelectedNumber] = useState<WhatsAppNumber | null>(
    null
  );
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentQR, setCurrentQR] = useState<string | null>(null);
  const [instanceStatus, setInstanceStatus] = useState<
    "loading" | "ready" | "qr" | "connected" | "connecting" | "failed"
  >("loading");
  const [formData, setFormData] = useState({
    phone_number: "",
    display_name: "",
  });

  useEffect(() => {
    fetchWhatsAppNumbers();

    return () => {
      // Clean up any active polling that might be happening
    };
  }, [organizationId]);

  const fetchWhatsAppNumbers = async () => {
    setIsLoading(true);
    try {
      const numbers = await whatsappService.getWhatsAppNumbers(organizationId);
      setWhatsappNumbers(numbers);
    } catch (error: any) {
      console.error("Error fetching WhatsApp numbers:", error);
      toast({
        title: "Error",
        description: "Failed to fetch WhatsApp numbers",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNumber = async () => {
    try {
      const newNumber = await whatsappService.addWhatsAppNumber({
        organization_id: organizationId,
        phone_number: formData.phone_number,
        display_name: formData.display_name,
      });

      setWhatsappNumbers([newNumber, ...whatsappNumbers]);
      setIsAddDialogOpen(false);
      setFormData({ phone_number: "", display_name: "" });

      toast({
        title: "Success",
        description: "WhatsApp number added successfully",
      });

      // Automatically show QR code for setup
      setSelectedNumber(newNumber);
      setIsQRDialogOpen(true);
    } catch (error: any) {
      console.error("Error adding WhatsApp number:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add WhatsApp number",
        variant: "destructive",
      });
    }
  };

  const handleDeleteNumber = async (number: WhatsAppNumber) => {
    try {
      await whatsappService.deleteWhatsAppNumber(number.id);
      setWhatsappNumbers(whatsappNumbers.filter((n) => n.id !== number.id));
      toast({
        title: "Success",
        description: "WhatsApp number deleted successfully",
      });
    } catch (error: any) {
      console.error("Error deleting WhatsApp number:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete WhatsApp number",
        variant: "destructive",
      });
    }
    setDeleteConfirmNumber(null);
  };

  const handleShowQRCode = async (number: WhatsAppNumber) => {
    console.log("handleShowQRCode called with number:", number);
    setSelectedNumber(number);
    setIsConnecting(true);
    setInstanceStatus("loading");
    setCurrentQR(null);

    try {
      // First, check if there's an existing connected instance
      const connectionStatus = await whatsappService.checkConnectionStatus(
        number.id
      );
      console.log("Connection status check result:", connectionStatus);

      // If the instance needs reauthentication, skip this check and go straight to QR code
      const needsReauthentication = connectionStatus.needsReauthentication || 
                                  number.session_data?.requires_new_qr ||
                                  connectionStatus.status === "not_found";
                                  
      if (needsReauthentication) {
        console.log('Instance needs reauthentication, skipping connection check');
        // Continue to QR code generation below
      }
      // If we have a persistent instance that's still connected, show success right away
      else if (
        connectionStatus.status === "connected" &&
        connectionStatus.isPersistent &&
        connectionStatus.instanceId &&
        !needsReauthentication
      ) {
        console.log(
          `Found existing connected instance: ${connectionStatus.instanceId}`
        );

        setInstanceStatus("connected");
        setIsConnecting(false);
        setIsQRDialogOpen(true);

        toast({
          title: "Already Connected",
          description: `WhatsApp is already connected with instance ID ${connectionStatus.instanceId}`,
          duration: 3000,
        });

        // Update the display
        fetchWhatsAppNumbers();
        return;
      }

      // Otherwise create a new instance
      console.log("Creating new instance...");
      const instance = await whatsappService.createInstance(number.id);
      console.log("Created instance:", instance);
      const instanceId = instance.id;

      // Update selected number with instance ID for cleanup
      setSelectedNumber((prev) =>
        prev
          ? {
              ...prev,
              session_data: { ...prev.session_data, instance_id: instanceId },
            }
          : null
      );

      // Function to set up polling for connection status
      const startStatusPolling = (newInstanceId: string) => {
        // Start polling for status changes
        whatsappService.pollConnectionStatus(
          number.id,
          newInstanceId,
          (status) => {
            console.log("Status update:", status);
            if (status === "connected") {
              setInstanceStatus("connected");
              setIsConnecting(false);

              toast({
                title: "Success",
                description: `WhatsApp connected successfully! Instance ID ${newInstanceId} has been stored for future use.`,
                duration: 3000,
              });

              // Refresh the list to show updated status
              fetchWhatsAppNumbers();

              // Close dialog after successful connection
              setTimeout(() => {
                setIsQRDialogOpen(false);
              }, 1500);
            } else if (status === "failed" || status === "disconnected") {
              // setInstanceStatus('failed');
              // setIsConnecting(false);
              // toast({
              //   title: "Error",
              //   description: "Failed to connect to WhatsApp",
              //   variant: "destructive",
              // });
            }
          }
        );
      };

      // Function to handle instance recreation if needed
      const recreateInstanceIfNeeded = async () => {
        setInstanceStatus("loading");

        toast({
          title: "Creating new instance",
          description: "Setting up new WhatsApp instance...",
        });

        try {
          // Delete the old instance and create a new one
          const newInstance = await whatsappService.deleteAndRecreateInstance(
            number.id
          );
          console.log("Created new instance:", newInstance);

          // Extract QR code directly from instance response
          let qrCode = newInstance.qrCode || newInstance.qr || "";
          console.log(
            "QR code from create instance:",
            typeof qrCode,
            qrCode ? qrCode.substring(0, 50) + "..." : "none"
          );

          // Update selected number with the new instance ID
          setSelectedNumber((prev) =>
            prev
              ? {
                  ...prev,
                  session_data: { instance_id: newInstance.id },
                }
              : null
          );

          // Display QR code from instance response
          if (qrCode) {
            setCurrentQR(qrCode);
            setInstanceStatus("qr");

            // Start polling for connection status
            startStatusPolling(newInstance.id);
          } else {
            // If no QR code in response, try to fetch it
            try {
              qrCode = await whatsappService.getInstanceQRCode(newInstance.id);
              setCurrentQR(qrCode);
              setInstanceStatus("qr");
              startStatusPolling(newInstance.id);
            } catch (qrError) {
              console.error("Error getting QR code:", qrError);
              setInstanceStatus("failed");
              setIsConnecting(false);

              toast({
                title: "Error",
                description: "Failed to generate QR code",
                variant: "destructive",
              });
            }
          }
        } catch (recreateError) {
          console.error("Error creating WhatsApp instance:", recreateError);
          setInstanceStatus("failed");
          setIsConnecting(false);

          toast({
            title: "Error",
            description: "Failed to create WhatsApp instance",
            variant: "destructive",
          });
        }
      };

      // Create fresh instance and get QR code
      recreateInstanceIfNeeded();

      // Always show QR dialog for new instance
      setIsQRDialogOpen(true);
    } catch (error: any) {
      console.error("Error setting up WhatsApp connection:", error);
      toast({
        title: "Error",
        description: "Failed to set up WhatsApp connection",
        variant: "destructive",
      });
      setIsConnecting(false);
      setInstanceStatus("failed");
    }
  };

  const getStatusBadge = (
    status: string,
    hasPersistedInstance: boolean = false
  ) => {
    switch (status) {
      case "active":
        return (
          <Badge className="bg-green-500">
            {hasPersistedInstance ? "Connected" : "Active"}
          </Badge>
        );
      case "pending_setup":
        return <Badge className="bg-yellow-500">Pending Setup</Badge>;
      case "inactive":
        return <Badge className="bg-gray-500">Inactive</Badge>;
      case "error":
        return <Badge className="bg-red-500">Error</Badge>;
      case "disconnected":
        return <Badge className="bg-orange-500">Disconnected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };
  
  // Function to check connection status without showing QR code
  const handleCheckStatus = async (number: WhatsAppNumber) => {
    try {
      toast({
        title: "Checking connection",
        description: "Verifying WhatsApp connection status...",
      });
      
      const connectionStatus = await whatsappService.checkConnectionStatus(number.id);
      console.log("Connection status check result:", connectionStatus);
      
      if (connectionStatus.status === "connected") {
        toast({
          title: "Connection Active",
          description: `WhatsApp is connected with instance ID ${connectionStatus.instanceId}`,
          duration: 3000,
        });
        
        // Update session data in UI
        const updatedNumbers = whatsappNumbers.map(n => {
          if (n.id === number.id) {
            return { 
              ...n, 
              status: "active",
              session_data: { 
                ...n.session_data, 
                last_connected: new Date().toISOString(),
                persistent: true
              }
            };
          }
          return n;
        });
        
        setWhatsappNumbers(updatedNumbers);
      } else if (connectionStatus.needsReauthentication || connectionStatus.status === "not_found") {
        // Instance no longer exists or is invalid - need to reauthenticate with QR code
        toast({
          title: "Authentication Required",
          description: "WhatsApp instance no longer exists. Please scan QR code to reauthenticate.",
          variant: "destructive",
          duration: 5000,
        });
        
        // Update UI to show error state
        const updatedNumbers = whatsappNumbers.map(n => {
          if (n.id === number.id) {
            return { 
              ...n, 
              status: "error",
              session_data: { 
                ...n.session_data, 
                requires_new_qr: true,
                error: "Instance not found - requires re-authentication" 
              }
            };
          }
          return n;
        });
        
        setWhatsappNumbers(updatedNumbers);
        
        // Show QR code scan dialog immediately
        setTimeout(() => {
          handleShowQRCode(number);
        }, 500);
      } else if (connectionStatus.status === "disconnected") {
        toast({
          title: "Connection Issue",
          description: "WhatsApp appears to be disconnected. Attempting to reconnect...",
          variant: "destructive",
          duration: 4000,
        });
        
        // Update status in UI first
        const updatedNumbers = whatsappNumbers.map(n => {
          if (n.id === number.id) {
            return { ...n, status: "disconnected" };
          }
          return n;
        });
        
        setWhatsappNumbers(updatedNumbers);
        
        // Ask the user if they want to try reconnecting automatically
        if (confirm("WhatsApp is disconnected. Would you like to attempt automatic reconnection?")) {
          await handleReconnect(number);
        }
      } else {
        toast({
          title: "Connection Issue",
          description: `WhatsApp status: ${connectionStatus.status}. Try reconnecting with QR code.`,
          variant: "destructive",
          duration: 4000,
        });
      }
      
      // Refresh the numbers list to show current status
      fetchWhatsAppNumbers();
    } catch (error) {
      console.error("Error checking connection status:", error);
      toast({
        title: "Error",
        description: "Failed to verify connection status",
        variant: "destructive",
      });
    }
  };
  
  // New function to handle reconnection attempts
  const handleSyncMessages = async (number: WhatsAppNumber) => {
    try {
      setIsSyncing(true);
      toast({
        title: "Syncing Messages",
        description: "Starting to sync messages from WhatsApp to database...",
      });
      
      const result = await whatsappService.syncAllLeadMessages(number.id);
      setSyncResults(result);
      
      // Create a detailed summary dialog for the user
      const summary = `
        **Sync Results Summary**
        
        - **Contacts Processed**: ${result.contactsProcessed}
        - **Leads with Messages**: ${result.leadsWithMessages} 
        - **Messages Found**: ${result.messagesFound}
        - **Messages Added**: ${result.messagesAdded || 'N/A'}
        - **Errors**: ${result.errors.length}
      `;
      
      if (result.errors.length === 0) {
        toast({
          title: "Sync Complete",
          description: `Successfully synced ${result.messagesFound} messages across ${result.leadsWithMessages} leads.`,
          duration: 5000,
        });
      } else {
        // More detailed error toast
        toast({
          title: "Sync Completed with Errors",
          description: `Synced ${result.messagesFound} messages with ${result.errors.length} errors. See console for details.`,
          variant: "destructive",
          duration: 6000,
        });
        
        // Log errors to console for debugging
        console.error('Sync error details:', result.errors);
      }
      
      // You could display this summary in a modal dialog
      alert(summary);
      
      // Show sync results dialog
      // You could add a dialog here to show detailed results if needed
    } catch (error) {
      console.error("Error syncing messages:", error);
      toast({
        title: "Sync Failed",
        description: "Failed to sync WhatsApp messages.",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleReconnect = async (number: WhatsAppNumber) => {
    try {
      toast({
        title: "Reconnecting",
        description: "Attempting to restore WhatsApp connection...",
      });
      
      // Set status to connecting in UI
      const updatedNumbers = whatsappNumbers.map(n => {
        if (n.id === number.id) {
          return { ...n, status: "pending_setup" };
        }
        return n;
      });
      
      setWhatsappNumbers(updatedNumbers);
      
      // Initialize QR code dialog with connecting state
      setSelectedNumber(number);
      setInstanceStatus("connecting");
      setIsQRDialogOpen(true);
      
      // Try to use existing credentials to reconnect
      const connectionStatus = await whatsappService.checkConnectionStatus(number.id);
      
      if (connectionStatus.instanceId) {
        // Try to revive existing instance
        await whatsappService.checkInstance(number.id);
        
        // Check status after attempt
        const newStatus = await whatsappService.checkConnectionStatus(number.id);
        
        if (newStatus.status === "connected") {
          // Successfully reconnected
          setInstanceStatus("connected");
          toast({
            title: "Reconnected",
            description: "Successfully reconnected to WhatsApp",
          });
          
          // Refresh data
          fetchWhatsAppNumbers();
          
          // Close dialog after success
          setTimeout(() => {
            setIsQRDialogOpen(false);
          }, 1500);
          
          return;
        }
      }
      
      // If we get here, automated reconnection failed - show QR code
      toast({
        title: "Reconnection Failed",
        description: "Could not automatically reconnect. Please scan the QR code.",
        variant: "destructive",
      });
      
      // Reset and show QR code
      setInstanceStatus("loading");
      setCurrentQR(null);
      handleShowQRCode(number);
      
    } catch (error) {
      console.error("Error during reconnection:", error);
      toast({
        title: "Reconnection Failed",
        description: "Failed to reconnect to WhatsApp. Please try scanning the QR code again.",
        variant: "destructive",
      });
      
      // Show QR code as fallback
      handleShowQRCode(number);
    }
  };

  if (isLoading) {
    return <div>Loading WhatsApp numbers...</div>;
  }
  
  if (isSyncing) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mb-4" />
          <h3 className="text-lg font-medium mb-2">Syncing WhatsApp Messages</h3>
          <p className="text-muted-foreground">This may take a few minutes depending on the number of contacts and messages.</p>
          {syncResults && (
            <div className="mt-4 text-sm bg-gray-50 p-4 rounded-md w-full max-w-lg">
              <p><strong>Progress:</strong></p>
              <ul className="list-disc pl-5">
                <li>Contacts processed: {syncResults.contactsProcessed}</li>
                <li>Leads with messages: {syncResults.leadsWithMessages}</li>
                <li>Messages found: {syncResults.messagesFound}</li>
                <li>Errors: {syncResults.errors.length}</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">WhatsApp Numbers</h3>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add WhatsApp Number
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add WhatsApp Number</DialogTitle>
              <DialogDescription>
                Add a new WhatsApp Business number to your organization
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="phone_number">Phone Number</Label>
                <Input
                  id="phone_number"
                  placeholder="+1234567890"
                  value={formData.phone_number}
                  onChange={(e) =>
                    setFormData({ ...formData, phone_number: e.target.value })
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="display_name">Display Name (Optional)</Label>
                <Input
                  id="display_name"
                  placeholder="My Business WhatsApp"
                  value={formData.display_name}
                  onChange={(e) =>
                    setFormData({ ...formData, display_name: e.target.value })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={handleAddNumber}
                disabled={!formData.phone_number}
              >
                Add Number
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Phone Number</TableHead>
            <TableHead>Display Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {whatsappNumbers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center">
                No WhatsApp numbers added yet
              </TableCell>
            </TableRow>
          ) : (
            whatsappNumbers.map((number) => {
              // Check if this number has a persisted WhatsApp instance
              const hasPersistedInstance =
                number.session_data &&
                number.session_data.persistent === true &&
                number.session_data.instance_id;
                
              // Get WhatsApp phone if available
              const whatsappPhone = number.session_data?.whatsapp_phone || 
                                   number.session_data?.phone_number;

              return (
                <TableRow key={number.id}>
                  <TableCell>
                    <div className="flex items-center">
                      <Phone className="mr-2 h-4 w-4" />
                      <div>
                        <div>{formatPhoneNumber(number.phone_number)}</div>
                        {whatsappPhone && whatsappPhone !== number.phone_number && (
                          <div className="text-xs text-gray-500">
                            WhatsApp ID: {whatsappPhone}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{number.display_name || "-"}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {getStatusBadge(number.status, hasPersistedInstance)}
                      {hasPersistedInstance && number.session_data?.last_connected && (
                        <div className="text-xs text-gray-500">
                          Last connected: {new Date(number.session_data.last_connected).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(number.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {/* For connected instances, show refresh button */}
                      {number.status === "active" && hasPersistedInstance && !number.session_data?.requires_new_qr ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCheckStatus(number)}
                            title="Check connection status"
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSyncMessages(number)}
                            title="Sync all messages for this WhatsApp number"
                            className="text-green-500 hover:text-green-700"
                            disabled={isSyncing}
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        /* For pending setup, error, or instances needing reauthentication, show QR code button */
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleShowQRCode(number)}
                          title={number.session_data?.requires_new_qr ? "Re-authenticate with QR code" : "Generate QR code"}
                          className={number.status === "error" || number.session_data?.requires_new_qr ? 
                                    "text-red-500 hover:text-red-700" : ""}
                        >
                          <QrCode className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteConfirmNumber(number)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* QR Code Dialog */}
      <Dialog
        open={isQRDialogOpen}
        onOpenChange={(open) => {
          if (!open && instanceStatus === "connecting") {
            // Ask for confirmation if closing during reconnection
            if (confirm("Are you sure you want to cancel the reconnection process?")) {
              setIsQRDialogOpen(false);
            } else {
              // Prevent dialog from closing if user cancels
              setIsQRDialogOpen(true);
              return;
            }
          } else {
            setIsQRDialogOpen(open);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Scan QR Code</DialogTitle>
            <DialogDescription>
              Scan this QR code with WhatsApp on your phone to connect
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center p-6">
            {instanceStatus === "loading" ? (
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                <p>Setting up WhatsApp connection...</p>
              </div>
            ) : instanceStatus === "connecting" ? (
              <div className="text-center space-y-4">
                <div className="h-16 w-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="h-8 w-8 animate-spin text-white" />
                </div>
                <p className="text-lg font-medium">
                  WhatsApp is reconnecting...
                </p>
                <p className="text-sm text-gray-600">
                  The WhatsApp connection was interrupted and is automatically
                  being restored. This might take a few moments.
                </p>
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800 mt-4">
                  <p className="font-medium">Reconnection in progress</p>
                  <p className="mt-1">
                    Your WhatsApp session is being restored using saved
                    credentials.
                  </p>
                  <p className="mt-3">
                    If automatic reconnection fails, a QR code will appear for manual reconnection.
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="mt-2" 
                  size="sm"
                  onClick={() => {
                    // Force show QR code
                    if (selectedNumber) {
                      handleShowQRCode(selectedNumber);
                    }
                  }}
                >
                  Skip to QR Code
                </Button>
              </div>
            ) : instanceStatus === "qr" && currentQR ? (
              <div className="space-y-4">
                <div className="p-4 bg-white rounded-lg">
                  {/* Handle different formats of QR code */}
                  {typeof currentQR === "string" ? (
                    (() => {
                      console.log(
                        "Rendering QR code string:",
                        currentQR.substring(0, 30) + "..."
                      );

                      // Handle "data:" prefix URLs
                      if (currentQR.indexOf("data:") === 0) {
                        return (
                          <img
                            src={currentQR}
                            alt="WhatsApp QR Code"
                            className="w-64 h-64 mx-auto"
                          />
                        );
                      }

                      // Handle base64 strings (ensure no [object Object])
                      try {
                        const base64Url = `data:image/png;base64,${currentQR}`;
                        // Test if URL is valid
                        new URL(base64Url);
                        return (
                          <img
                            src={base64Url}
                            alt="WhatsApp QR Code"
                            className="w-64 h-64 mx-auto"
                            onError={(e) => {
                              console.error("Error loading QR code image:", e);
                              // Replace with error message
                              e.currentTarget.style.display = "none";
                              e.currentTarget.parentElement?.appendChild(
                                document.createTextNode(
                                  "Error displaying QR code"
                                )
                              );
                            }}
                          />
                        );
                      } catch (e) {
                        console.error("Invalid QR code URL:", e);
                        return (
                          <div className="text-center p-4 bg-red-50 text-red-500 rounded-md">
                            Invalid QR Code format
                          </div>
                        );
                      }
                    })()
                  ) : (
                    // If it's not a string at all, display an error
                    <div className="text-center p-4 bg-red-50 text-red-500 rounded-md">
                      QR Code format not supported
                    </div>
                  )}
                </div>
                {isConnecting && (
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-500" />
                    <p className="text-gray-700">
                      Waiting for WhatsApp to connect...
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Open WhatsApp on your phone and scan the QR code
                    </p>
                  </div>
                )}
              </div>
            ) : instanceStatus === "connected" ? (
              <div className="text-center">
                <div className="h-16 w-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="h-8 w-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p className="text-green-600 font-medium">
                  Connected Successfully!
                </p>
              </div>
            ) : instanceStatus === "failed" ? (
              <div className="text-center">
                <div className="h-16 w-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="h-8 w-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <p className="text-red-600 font-medium">Connection Failed</p>
                <p className="text-sm text-gray-600 mt-2">Please try again</p>
              </div>
            ) : (
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                <p>Waiting for QR code...</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setIsQRDialogOpen(false);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteConfirmNumber}
        onOpenChange={() => setDeleteConfirmNumber(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the WhatsApp number{" "}
              <strong>{deleteConfirmNumber?.phone_number}</strong>. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteConfirmNumber && handleDeleteNumber(deleteConfirmNumber)
              }
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
